/**
 * Reverse-geocode coordinates into a human-readable address + city.
 *
 * Strategy: try the Google Geocoding REST API first (using the existing browser
 * Maps key), then fall back to Nominatim (OpenStreetMap) when Google is
 * unavailable, returns no results, or the key is missing. On total failure the
 * caller gets `{ address: null, city: null }` and can fall back to raw coords.
 */

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? ''

export interface ReverseGeocodeData {
  address: string | null
  city: string | null
}

/** Decimals kept when building cache keys (~1.1m precision at 5 dp). */
const COORD_PRECISION = 5
const CACHE_PREFIX = 'revgeo:'

/** Normalize a coordinate so near-identical points share one cache entry. */
export function roundCoord(value: number): number {
  const factor = 10 ** COORD_PRECISION
  return Math.round(value * factor) / factor
}

function cacheKey(lat: number, lng: number): string {
  return `${CACHE_PREFIX}${roundCoord(lat)},${roundCoord(lng)}`
}

function readCache(lat: number, lng: number): ReverseGeocodeData | null {
  try {
    const raw = localStorage.getItem(cacheKey(lat, lng))
    return raw ? (JSON.parse(raw) as ReverseGeocodeData) : null
  } catch {
    return null
  }
}

function writeCache(lat: number, lng: number, data: ReverseGeocodeData): void {
  try {
    localStorage.setItem(cacheKey(lat, lng), JSON.stringify(data))
  } catch {
    // ignore quota / unavailable storage
  }
}

interface GoogleAddressComponent {
  long_name: string
  short_name: string
  types: string[]
}

interface GoogleGeocodeResponse {
  status: string
  results?: Array<{
    formatted_address?: string
    address_components?: GoogleAddressComponent[]
  }>
}

interface NominatimResponse {
  display_name?: string
  address?: {
    city?: string
    town?: string
    village?: string
    state?: string
    county?: string
  }
}

function getCityFromGoogleComponents(
  components: GoogleAddressComponent[] | undefined
): string | null {
  if (!components?.length) return null
  const byType = (type: string) =>
    components.find((c) => c.types.includes(type))?.long_name ?? null
  return (
    byType('locality') ??
    byType('administrative_area_level_2') ??
    byType('administrative_area_level_1') ??
    null
  )
}

async function reverseGeocodeWithGoogle(lat: number, lng: number): Promise<ReverseGeocodeData> {
  if (!GOOGLE_MAPS_API_KEY) throw new Error('Missing Google Maps API key')
  const url = new URL('https://maps.googleapis.com/maps/api/geocode/json')
  url.searchParams.set('latlng', `${lat},${lng}`)
  url.searchParams.set('language', 'en')
  url.searchParams.set('key', GOOGLE_MAPS_API_KEY)

  const res = await fetch(url.toString())
  if (!res.ok) throw new Error(`Google geocode ${res.status}`)
  const data = (await res.json()) as GoogleGeocodeResponse
  if (data.status !== 'OK' || !data.results?.[0]) {
    throw new Error(`Google geocode status ${data.status}`)
  }
  const first = data.results[0]
  return {
    address: first.formatted_address ?? null,
    city: getCityFromGoogleComponents(first.address_components),
  }
}

async function reverseGeocodeWithNominatim(lat: number, lng: number): Promise<ReverseGeocodeData> {
  const url = new URL('https://nominatim.openstreetmap.org/reverse')
  url.searchParams.set('lat', String(lat))
  url.searchParams.set('lon', String(lng))
  url.searchParams.set('format', 'json')
  url.searchParams.set('addressdetails', '1')
  url.searchParams.set('accept-language', 'en')
  // Note: a User-Agent header cannot be set from the browser (fetch forbids it),
  // so we rely on the default. Nominatim still responds for low-volume use.

  const res = await fetch(url.toString())
  if (!res.ok) throw new Error(`Nominatim ${res.status}`)
  const data = (await res.json()) as NominatimResponse
  const addr = data.address
  return {
    address: data.display_name ?? null,
    city: addr?.city ?? addr?.town ?? addr?.village ?? addr?.state ?? addr?.county ?? null,
  }
}

/**
 * Reverse-geocode a coordinate pair. Tries Google, then Nominatim.
 *
 * Results are cached in `localStorage` (keyed by coordinates rounded to
 * {@link COORD_PRECISION} decimals) so the same point is never re-requested
 * across reloads. Failures are NOT cached, so they can be retried later.
 * Returns `{ address: null, city: null }` if both providers fail.
 */
export async function reverseGeocode(lat: number, lng: number): Promise<ReverseGeocodeData> {
  const cached = readCache(lat, lng)
  if (cached) return cached

  let result: ReverseGeocodeData = { address: null, city: null }
  try {
    result = await reverseGeocodeWithGoogle(lat, lng)
  } catch {
    try {
      result = await reverseGeocodeWithNominatim(lat, lng)
    } catch {
      // both providers failed
    }
  }

  // Only persist successful resolutions so failures can be retried.
  if (result.address || result.city) writeCache(lat, lng, result)
  return result
}
